// SPDX-License-Identifier: AGPL-3.0-or-later

import {Routes} from '@app/app/Routes';
import {openExternalUrlWithWarning} from '@app/features/messaging/utils/ExternalLinkUtils';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import AppStorage from '@app/features/platform/state/PersistentStorage';
import * as PremiumModalCommands from '@app/features/premium/commands/PremiumModalCommands';
import {
	type InstanceAnnouncementPublic,
	isInstanceAnnouncementUrl,
} from '@fluxer/schema/src/domains/instance/InstanceAnnouncementSchemas';

const DISMISS_KEY_PREFIX = 'micup_instance_announcement_dismissed:';

export interface VisibleInstanceAnnouncement {
	readonly id: string;
	readonly icon: string | null;
	readonly text: string;
	readonly buttonText: string | null;
	readonly target: InstanceAnnouncementPublic['target'];
	readonly url: string | null;
	readonly tone: InstanceAnnouncementPublic['tone'];
}

export function isInstanceAnnouncementDismissed(id: string): boolean {
	return AppStorage.getItem(`${DISMISS_KEY_PREFIX}${id}`) === '1';
}

export function dismissInstanceAnnouncement(id: string): void {
	AppStorage.setItem(`${DISMISS_KEY_PREFIX}${id}`, '1');
}

function isLive(announcement: InstanceAnnouncementPublic, now: number): boolean {
	if (announcement.starts_at !== null && now < Date.parse(announcement.starts_at)) return false;
	return announcement.ends_at === null || now < Date.parse(announcement.ends_at);
}

// When the announcement next starts or ends after `now`, so an open app shows or hides it on time; null: never.
export function nextInstanceAnnouncementBoundary(
	announcement: InstanceAnnouncementPublic | null,
	now: number,
): number | null {
	if (!announcement) return null;
	const ahead = [announcement.starts_at, announcement.ends_at]
		.map((at) => (at === null ? Number.NaN : Date.parse(at)))
		.filter((at) => at > now);
	return ahead.length === 0 ? null : Math.min(...ahead);
}

// Turkish to Turkish speakers, English to everyone else when written, Turkish otherwise.
function pick(locale: string, tr: string | null, en: string | null): string | null {
	return locale.toLowerCase().startsWith('tr') || en === null ? tr : en;
}

export function resolveInstanceAnnouncement(
	announcement: InstanceAnnouncementPublic | null,
	now: number,
	locale: string,
): VisibleInstanceAnnouncement | null {
	if (!announcement || !isLive(announcement, now) || isInstanceAnnouncementDismissed(announcement.id)) return null;
	const text = pick(locale, announcement.text_tr, announcement.text_en);
	if (text === null) return null;
	return {
		id: announcement.id,
		icon: announcement.icon,
		text,
		buttonText:
			announcement.target === 'none' ? null : pick(locale, announcement.button_text_tr, announcement.button_text_en),
		target: announcement.target,
		url: announcement.url,
		tone: announcement.tone,
	};
}

export function openInstanceAnnouncementTarget({
	target,
	url,
}: Pick<VisibleInstanceAnnouncement, 'target' | 'url'>): void {
	if (target === 'shop') RouterUtils.transitionTo(Routes.SHOP);
	else if (target === 'premium') PremiumModalCommands.open();
	else if (target === 'url' && url !== null && isInstanceAnnouncementUrl(url)) openExternalUrlWithWarning(url);
}
